import { useState } from 'react'
import type { Role, StoryEntry } from '@shared/types'
import { ApiError, api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Input, Modal, Panel, SectionHeading, Select, Textarea,
} from '@/components/ui'

interface Draft {
  id: string | null
  title: string
  body: string
  visibility: 'dm_only' | 'players'
  sessionDate: string
  tags: string
}

const BLANK: Draft = {
  id: null, title: '', body: '', visibility: 'players',
  sessionDate: new Date().toISOString().slice(0, 10), tags: '',
}

export function StoryTab({ campaignId, myRole }: { campaignId: string; myRole: Role }) {
  const { data, error, loading, reload } = useAsync(() => api.listStory(campaignId), [campaignId])
  const [draft, setDraft] = useState<Draft | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const isDm = myRole === 'dm'

  // Entries are already loaded, so filtering here beats a round trip.
  const needle = query.trim().toLowerCase()
  const visible = (data?.entries ?? []).filter((entry) =>
    !needle || [entry.title, entry.body, entry.tags.join(' ')].join(' ').toLowerCase().includes(needle))

  async function save() {
    if (!draft) return
    setActionError(null)
    const payload = {
      title: draft.title.trim(),
      body: draft.body,
      visibility: draft.visibility,
      sessionDate: draft.sessionDate || null,
      tags: draft.tags.split(',').map((t) => t.trim()).filter(Boolean),
    }
    try {
      if (draft.id) await api.updateStory(campaignId, draft.id, payload)
      else await api.createStory(campaignId, payload)
      setDraft(null)
      reload()
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not save that entry')
    }
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        action={
          <div className="flex items-center gap-2">
            {(data?.entries.length ?? 0) > 0 && (
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search entries…"
                aria-label="Search the story log"
                className="h-8 w-44 py-0 text-[13px]"
              />
            )}
            {isDm && <Button size="sm" variant="primary" onClick={() => setDraft({ ...BLANK })}>New entry</Button>}
          </div>
        }
      >
        Story log
      </SectionHeading>

      {(error || actionError) && <ErrorNote>{actionError ?? error}</ErrorNote>}

      {loading ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>
      ) : !data?.entries.length ? (
        <EmptyState
          title="No entries yet"
          description={isDm
            ? 'Write a recap after each session. Players see anything marked visible to them; DM-only entries stay yours.'
            : 'Your DM has not posted any recaps yet.'}
          action={isDm ? <Button variant="primary" onClick={() => setDraft({ ...BLANK })}>Write the first one</Button> : undefined}
        />
      ) : visible.length === 0 ? (
        <Panel className="p-6 text-center text-sm text-ink-500">Nothing matches “{query}”.</Panel>
      ) : (
        <div className="space-y-4">
          {visible.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              isDm={isDm}
              onEdit={() => setDraft({
                id: entry.id,
                title: entry.title,
                body: entry.body,
                visibility: entry.visibility,
                sessionDate: entry.sessionDate ?? '',
                tags: entry.tags.join(', '),
              })}
              onDelete={async () => {
                setActionError(null)
                try {
                  await api.deleteStory(campaignId, entry.id)
                  reload()
                } catch (err) {
                  setActionError(err instanceof ApiError ? err.message : 'Could not delete that')
                }
              }}
            />
          ))}
        </div>
      )}

      <Modal
        open={draft !== null}
        title={draft?.id ? 'Edit entry' : 'New story entry'}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <div className="space-y-4">
            <Field label="Title">
              {(id) => (
                <Input id={id} value={draft.title} maxLength={160} autoFocus
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Session 3 — The Hourglass Coven" />
              )}
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Session date">
                {(id) => (
                  <Input id={id} type="date" value={draft.sessionDate}
                    onChange={(e) => setDraft({ ...draft, sessionDate: e.target.value })} />
                )}
              </Field>
              <Field label="Who can read it">
                {(id) => (
                  <Select id={id} value={draft.visibility}
                    onChange={(e) => setDraft({ ...draft, visibility: e.target.value as Draft['visibility'] })}>
                    <option value="players">Players can read this</option>
                    <option value="dm_only">DM only</option>
                  </Select>
                )}
              </Field>
            </div>

            <Field label="What happened">
              {(id) => (
                <Textarea id={id} value={draft.body} maxLength={50000} className="min-h-48"
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  placeholder="Where they went, who they met, what they promised…" />
              )}
            </Field>

            <Field label="Tags" hint="Comma separated. Handy for finding things later.">
              {(id) => (
                <Input id={id} value={draft.tags} maxLength={300}
                  onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                  placeholder="carnival, bavlorna, lost things" />
              )}
            </Field>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
              <Button variant="primary" disabled={!draft.title.trim()} onClick={save}>Save entry</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function EntryCard({
  entry, isDm, onEdit, onDelete,
}: {
  entry: StoryEntry
  isDm: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const secret = entry.visibility === 'dm_only'
  const long = entry.body.length > 400

  return (
    <Panel className={secret ? 'border-arcane-500/40 p-5' : 'p-5'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg leading-tight">{entry.title}</h3>
          <p className="mt-1 text-xs text-ink-500">
            {entry.sessionDate
              ? new Date(`${entry.sessionDate}T00:00:00`).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'long', day: 'numeric',
                })
              : 'No session date'}
            {entry.authorName && ` · ${entry.authorName}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {secret && <Badge tone="arcane">DM only</Badge>}
          {isDm && (
            <>
              <Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={onDelete}>Delete</Button>
            </>
          )}
        </div>
      </div>

      {entry.body && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink-300">
          {long && !expanded ? `${entry.body.slice(0, 400).trimEnd()}…` : entry.body}
        </p>
      )}
      {long && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 text-xs text-gold-400 underline-offset-2 hover:underline"
        >
          {expanded ? 'Show less' : 'Read the rest'}
        </button>
      )}

      {entry.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-ink-700/50 pt-3">
          {entry.tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
        </div>
      )}
    </Panel>
  )
}
